import { Grid, Typography } from '@mui/material';
import Timeline from '@mui/icons-material/Timeline';
import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { MatterStatus } from '~/gql/graphql';
import type { UpdateMatterInput } from '~/gql/graphql';
import { MatterDetailStatus } from '~/modules/matters/matterDetailStatus';
import { useMatterDetail } from '~/modules/matters/useMatterDetail.ts';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import { UserRole } from '~/shared/types/connect';
import RequestRetainerModal from '~/modules/matters/modals/RequestRetainerModal';
import RetainerAgreementModal from '~/modules/matters/modals/RetainerAgreementModal';
import RetainerAgreementOverviewModal from '~/modules/matters/modals/RetainerAgreementOverviewModal';
import DeclineRetainerDialog from '~/modules/matters/modals/DeclineRetainerDialog';
import CloseRetainerModal from '~/modules/matters/modals/CloseRetainerModal';
import CloseMatterModal from '~/modules/matters/modals/CloseMatterModal';
import AddExpertsModal from '~/modules/matters/modals/AddExpertsModal';
import RemoveExpertModal from '~/modules/matters/modals/RemoveExpertModal';
import DeclineMatterModal from '~/modules/matters/modals/DeclineMatterModal';
import {
  CreateMatterModal,
  type CreateMatterFormData,
} from '~/modules/matters/modals/CreateMatterModal';
import { ActivityFeed } from '~/modules/matters';
import { MatterDetailSkeleton } from '~/modules/matters/MatterDetailSkeleton';
import { MessageCard } from '~/shared/components/MessageCard';
import ChatSidebar from '~/shared/components/ChatSidebar';
import {
  MatterDetailHeader,
  MatterDetailRightColumn,
  MatterDetailsPanel,
  ExpertsTabContent,
  RetainersTabContent,
  OtherPartyCard,
} from '~/modules/matters/detail';
import {
  RightColumnHeader,
  RightColumnTitle,
} from '~/modules/matters/detail/MatterDetailRightColumn';
import {
  RetainersEmptyCard,
  ActivityColumnPaper,
  ActivitySectionHeader,
  RightColumnPaper,
  DetailPageRoot,
} from '~/modules/matters/detail/MatterDetail.styles';
import { useToast } from '~/shared/contexts/ToastContext';
import { useMatterUnreadCounts } from '~/hooks/useMatterUnreadCounts';
import {
  getEffectiveRetainerExpertId,
  getRetainerModalMatterExpertId,
} from '~/modules/matters/matterDetailRetainerModal';

const MatterDetail = () => {
  const { showToast } = useToast();
  const [isUpdatingMatter, setIsUpdatingMatter] = useState(false);
  const [matterToClose, setMatterToClose] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const { matterContext, retainers, experts, modals, chat, actions, store } =
    useMatterDetail();

  const matterId = matterContext.matterId ?? '';
  const { byExpert: unreadByExpert } = useMatterUnreadCounts({
    matterId,
    skip: !matterId,
  });
  const getUnreadCountForExpert = (expertId: string) =>
    unreadByExpert[expertId] ?? 0;

  const openRequestRetainerModal = () => {
    if (
      matterContext.currentUser?.role === UserRole.EXPERT &&
      !matterContext.isExpertOnMatter &&
      !matterContext.isInvitedOnlyExpert
    ) {
      showToast(
        'You must be invited to this matter before you can request a retainer.',
        'error',
      );
      return;
    }
    modals.setRetainerModalExpertId(null);
    modals.setRetainerModalOpen(true);
  };

  const {
    updateMatter: updateMatterMutation,
    closeMatter: closeMatterMutation,
    isClosing: isClosingMatter,
  } = useMatterMutations();
  const handleEditMatterClick = () => {
    if (matterContext.matter) {
      modals.setMatterToEdit(matterContext.matter);
      modals.setEditMatterModalOpen(true);
    }
  };

  const handleUpdateMatter = async (data: CreateMatterFormData) => {
    if (!modals.matterToEdit) {
      return;
    }
    const matterId = modals.matterToEdit.id;
    if (!matterId) {
      return;
    }
    setIsUpdatingMatter(true);
    try {
      const input: UpdateMatterInput = {
        title: data.title,
        caseDescription: data.caseDescription,
        caseNumber: data.caseNumber || null,
        courtName: data.courtName || null,
        location: data.location || null,
        keyDates: data.keyDates || null,
        nextDeadline: data.nextDeadline || null,
        travelPlans: data.travelPlans || null,
        writtenReportNeeded: data.writtenReportNeeded || null,
      };
      await updateMatterMutation(matterId, input);
      modals.setEditMatterModalOpen(false);
      modals.setMatterToEdit(null);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_update_from_detail' },
        extra: { matterId },
      });
      const message =
        error instanceof Error ? error.message : 'Failed to update matter';
      showToast(message, 'error');
    } finally {
      setIsUpdatingMatter(false);
    }
  };

  // Only attorneys can change matter status; the dropdown is hidden for experts
  // and `updateMatter` / `closeMatter` are attorney-scoped on the backend.
  const handleStatusChange = async (value: MatterStatus) => {
    const matter = matterContext.matter;
    if (!matter || !matterContext.isAttorney) {
      return;
    }
    if (value === MatterStatus.Closed) {
      // Snapshot id + title at open time so the confirmation always refers to
      // the matter the user clicked, even if route/cache changes mid-flow.
      setMatterToClose({ id: matter.id, title: matter.title });
      return;
    }
    try {
      await updateMatterMutation(matter.id, { status: value });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_status_from_detail' },
        extra: { matterId: matter.id, status: value },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not update matter status. Please try again.';
      showToast(message, 'error');
    }
  };

  const handleConfirmCloseMatter = async () => {
    if (!matterToClose) {
      return;
    }
    try {
      await closeMatterMutation(matterToClose.id);
      setMatterToClose(null);
      matterContext.navigate('/matters');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_close_from_detail' },
        extra: { matterId: matterToClose.id },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Could not close matter. Please try again.';
      showToast(message, 'error');
    }
  };

  if (matterContext.status === MatterDetailStatus.MattersLoading) {
    return <MatterDetailSkeleton />;
  }

  if (matterContext.status === MatterDetailStatus.MattersError) {
    return (
      <MessageCard
        title="Could not load matter"
        description={
          matterContext.mattersQueryError?.message ??
          'Something went wrong while loading this matter. Try again.'
        }
        buttonLabel="Back to Matters"
        onButtonClick={() => matterContext.navigate('/matters')}
      />
    );
  }

  if (
    matterContext.status === MatterDetailStatus.NotFound ||
    matterContext.status === MatterDetailStatus.NoAccess
  ) {
    const title =
      matterContext.status === MatterDetailStatus.NotFound
        ? 'Matter not found'
        : "You don't have access";
    const description =
      matterContext.status === MatterDetailStatus.NotFound
        ? 'This matter could not be found.'
        : "You don't have access to this matter.";
    return (
      <MessageCard
        title={title}
        description={description}
        buttonLabel="Back to Matters"
        onButtonClick={() => matterContext.navigate('/matters')}
      />
    );
  }

  if (matterContext.status === MatterDetailStatus.RedirectStripe) {
    return null;
  }

  if (!matterContext.matter) {
    return <MatterDetailSkeleton />;
  }

  const expertStripeConnected =
    matterContext.currentUser?.role === 'Expert' &&
    Boolean(matterContext.currentUser.stripeConnectOnboarded);

  const showOtherPartyCard =
    !matterContext.isAttorney &&
    Boolean(
      matterContext.otherParty &&
        matterContext.matter &&
        matterContext.currentUser,
    );

  const detailsContent = (
    <MatterDetailsPanel
      matter={matterContext.matter}
      matterId={matterContext.matterId}
      isInvitedOnlyExpert={matterContext.isInvitedOnlyExpert}
      isAttorney={matterContext.isAttorney}
      expertStripeConnected={
        matterContext.isInvitedOnlyExpert ? expertStripeConnected : false
      }
      nextDeadlineEdit={modals.nextDeadlineEdit}
      setNextDeadlineEdit={modals.setNextDeadlineEdit}
      showDeadlinePicker={modals.showDeadlinePicker}
      setShowDeadlinePicker={modals.setShowDeadlinePicker}
      updateMatter={actions.updateMatter}
    />
  );

  const expertsContent =
    experts.showExpertsTab && matterContext.matter ? (
      <ExpertsTabContent
        hasAnyExperts={experts.hasAnyExperts}
        expertStatusFilter={modals.expertStatusFilter}
        setExpertStatusFilter={modals.setExpertStatusFilter}
        filteredHiredExperts={experts.filteredHiredExperts}
        expertsOnMatter={retainers.expertsOnMatter}
        isMatterClosed={matterContext.isMatterClosed}
        onOpenChat={chat.openChatWithExpert}
        onRemoveExpert={(expertId: string, isInvited: boolean) =>
          modals.setRemoveExpertModal({ expertId, isInvited })
        }
        getUnreadCountForExpert={getUnreadCountForExpert}
      />
    ) : null;

  const retainersTabContentEmpty = (
    <RetainersEmptyCard variant="outlined">
      <Typography variant="body2" color="text.secondary">
        Invite experts to this matter. They become active when they request a
        retainer and you accept.
      </Typography>
    </RetainersEmptyCard>
  );

  const retainersTabProps = {
    matter: matterContext.matter,
    isMatterClosed: matterContext.isMatterClosed,
    isAttorney: matterContext.isAttorney,
    isExpertOnMatter: matterContext.isExpertOnMatter,
    canExpertRequestRetainer: matterContext.canExpertRequestRetainer,
    expertStripeConnected,
    balance: retainers.balance,
    pendingRequests: retainers.pendingRequests,
    activeRetainers: retainers.activeRetainers,
    flatRateRetainers: retainers.flatRateRetainers,
    filteredPendingRequests: retainers.filteredPendingRequests,
    filteredFlatRateRetainers: retainers.filteredFlatRateRetainers,
    filteredActiveRetainers: retainers.filteredActiveRetainers,
    historyRetainers: retainers.historyRetainers,
    isLoadingRetainers: retainers.isLoadingRetainers,
    retainersError: retainers.retainersError,
    noRetainerBalance: retainers.noRetainerBalance,
    hasHoursBalance: retainers.hasHoursBalance,
    pendingApproveLabel: retainers.pendingApproveLabel,
    retainerStatusFilter: modals.retainerStatusFilter,
    setRetainerStatusFilter: modals.setRetainerStatusFilter,
    retainerExpertFilter: modals.retainerExpertFilter,
    setRetainerExpertFilter: modals.setRetainerExpertFilter,
    retainerExpertsForFilter: retainers.retainerExpertsForFilter,
    getExpertById: matterContext.getExpertById,
    setAgreementRequestId: modals.setAgreementRequestId,
    setDeclineRequestId: modals.setDeclineRequestId,
    onOpenRequestRetainerModal: openRequestRetainerModal,
    markRetainerWorkComplete: actions.markRetainerWorkComplete,
    completingRetainerId: retainers.completingRetainerId,
    setCloseRetainerModal: modals.setCloseRetainerModal,
    setViewAgreementRequestId: modals.setViewAgreementRequestId,
    setViewOverviewRequestId: modals.setViewOverviewRequestId,
    openChatWithExpert: chat.openChatWithExpert,
    markRetainerActive: actions.markRetainerActive,
    activatingRetainerId: retainers.activatingRetainerId,
    getUnreadCountForExpert,
  };

  const agreementModalLookupId =
    modals.agreementRequestId ?? modals.viewAgreementRequestId;
  const agreementModalRetainer =
    retainers.retainerRequests.find(
      (retainerItem) => retainerItem.id === agreementModalLookupId,
    ) ?? null;
  const overviewModalRetainer =
    retainers.retainerRequests.find(
      (retainerItem) => retainerItem.id === modals.viewOverviewRequestId,
    ) ?? null;

  const showRightColumnWithTabs =
    experts.showActivityAndBilling ||
    (experts.hasNoHiredExpert && matterContext.isAttorney) ||
    matterContext.isInvitedOnlyExpert;
  const rightColumnRetainersContent =
    experts.showActivityAndBilling || matterContext.isInvitedOnlyExpert ? (
      <RetainersTabContent {...retainersTabProps} />
    ) : (
      retainersTabContentEmpty
    );

  const effectiveRetainerExpertId =
    matterContext.matter === null
      ? undefined
      : getEffectiveRetainerExpertId({
          matterExperts: matterContext.matter.matterExperts,
          retainerModalExpertId: modals.retainerModalExpertId,
          currentUserRole: matterContext.currentUser?.role,
          currentUserId: matterContext.currentUser?.id,
        });

  const retainerModalMatterExpertId =
    matterContext.matter === null
      ? null
      : getRetainerModalMatterExpertId({
          matterExperts: matterContext.matter.matterExperts,
          effectiveRetainerExpertId,
        });
  const retainerModalExpert = effectiveRetainerExpertId
    ? matterContext.getExpertById(effectiveRetainerExpertId) ?? null
    : null;

  const declineLinkLabel = ((): string | undefined => {
    if (matterContext.isInvitedOnlyExpert) {
      return 'Decline';
    }
    if (matterContext.isExpertOnMatter) {
      return 'Leave';
    }
    return undefined;
  })();

  return (
    <DetailPageRoot>
      <MatterDetailHeader
        matter={matterContext.matter}
        isAttorney={matterContext.isAttorney}
        isExpertOnMatter={matterContext.isExpertOnMatter}
        isInvitedOnlyExpert={matterContext.isInvitedOnlyExpert}
        onStatusChange={handleStatusChange}
        declineLinkLabel={declineLinkLabel}
        onOpenDecline={
          matterContext.isExpertOnMatter || matterContext.isInvitedOnlyExpert
            ? () => modals.setDeclineMatterModalOpen(true)
            : undefined
        }
      />

      {showOtherPartyCard && matterContext.otherParty && (
        <OtherPartyCard
          otherParty={matterContext.otherParty}
          canRequestRetainer={matterContext.canExpertRequestRetainer}
          expertStripeConnected={expertStripeConnected}
          onRequestRetainer={openRequestRetainerModal}
          onMessage={() => {
            modals.setChatSidebarOpen(true);
          }}
          unreadCount={chat.unreadCount}
        />
      )}

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 2, md: 0 } }}>
          <ActivityColumnPaper variant="outlined">
            <ActivitySectionHeader>
              <Timeline sx={{ fontSize: 20, color: 'grey.700' }} aria-hidden />
              <Typography
                variant="h3"
                fontWeight={600}
                sx={{ color: 'grey.800' }}
              >
                Activity
              </Typography>
            </ActivitySectionHeader>
            {matterContext.currentUser && <ActivityFeed matterId={matterId} />}
          </ActivityColumnPaper>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }} sx={{ order: { xs: 1, md: 0 } }}>
          {showRightColumnWithTabs ? (
            <MatterDetailRightColumn
              rightDetailTab={modals.rightDetailTab}
              setRightDetailTab={modals.setRightDetailTab}
              showExpertsTab={experts.showExpertsTab}
              isAttorney={matterContext.isAttorney}
              onEditMatterClick={handleEditMatterClick}
              onAddExpertsClick={() => modals.setAddExpertsModalOpen(true)}
              retainersContent={rightColumnRetainersContent}
              expertsContent={expertsContent}
              detailsContent={detailsContent}
            />
          ) : (
            <RightColumnPaper variant="outlined">
              <RightColumnHeader>
                <RightColumnTitle variant="h3">Matter details</RightColumnTitle>
              </RightColumnHeader>
              {detailsContent}
            </RightColumnPaper>
          )}
        </Grid>
      </Grid>

      {matterContext.matter && effectiveRetainerExpertId && (
        <RequestRetainerModal
          open={modals.retainerModalOpen}
          onClose={() => {
            modals.setRetainerModalOpen(false);
            modals.setRetainerModalExpertId(null);
          }}
          matterId={matterId}
          matterExpertId={retainerModalMatterExpertId}
          expert={retainerModalExpert}
          rateSourceUser={matterContext.currentUser}
        />
      )}
      <RetainerAgreementModal
        isOpen={Boolean(
          modals.agreementRequestId || modals.viewAgreementRequestId,
        )}
        onClose={() => {
          modals.setAgreementRequestId(null);
          modals.setViewAgreementRequestId(null);
        }}
        matterId={matterId}
        retainer={agreementModalRetainer}
        isLoading={retainers.isLoadingRetainers && !agreementModalRetainer}
        getExpertById={matterContext.getExpertById}
        viewOnly={Boolean(modals.viewAgreementRequestId)}
        currentUserRole={matterContext.currentUser?.role}
      />
      <RetainerAgreementOverviewModal
        isOpen={Boolean(modals.viewOverviewRequestId)}
        onClose={() => modals.setViewOverviewRequestId(null)}
        retainer={overviewModalRetainer}
        isLoading={retainers.isLoadingRetainers && !overviewModalRetainer}
        getExpertById={matterContext.getExpertById}
      />
      <DeclineRetainerDialog
        isOpen={Boolean(modals.declineRequestId)}
        onClose={() => modals.setDeclineRequestId(null)}
        matterId={matterId}
        request={
          modals.declineRequestId
            ? retainers.retainerRequests.find(
                (retainerItem) => retainerItem.id === modals.declineRequestId,
              ) ?? null
            : null
        }
        onDeclined={() => modals.setDeclineRequestId(null)}
      />
      <CloseRetainerModal
        isOpen={Boolean(modals.closeRetainerModal)}
        onClose={() => modals.setCloseRetainerModal(null)}
        matterId={matterId}
        retainer={
          modals.closeRetainerModal
            ? retainers.retainerRequests.find(
                (retainerItem) =>
                  retainerItem.id === modals.closeRetainerModal?.retainerId,
              ) ?? null
            : null
        }
        onClosed={() => modals.setCloseRetainerModal(null)}
      />
      {matterContext.isAttorney && (
        <CloseMatterModal
          isOpen={Boolean(matterToClose)}
          onClose={() => setMatterToClose(null)}
          matterTitle={matterToClose?.title}
          onConfirm={handleConfirmCloseMatter}
          isClosing={isClosingMatter}
        />
      )}
      {matterContext.matter &&
        matterContext.currentUser &&
        (matterContext.isExpertOnMatter ||
          matterContext.isInvitedOnlyExpert) && (
          <DeclineMatterModal
            isOpen={modals.declineMatterModalOpen}
            onClose={() => modals.setDeclineMatterModalOpen(false)}
            matterId={matterId}
            expertId={matterContext.currentUser.id}
            isInvitedOnly={matterContext.isInvitedOnlyExpert}
            onSuccess={() => {
              modals.setDeclineMatterModalOpen(false);
              matterContext.navigate('/matters');
            }}
          />
        )}
      {matterContext.isAttorney && matterContext.matter && (
        <AddExpertsModal
          isOpen={modals.addExpertsModalOpen}
          onClose={() => modals.setAddExpertsModalOpen(false)}
          matter={matterContext.matter}
        />
      )}

      {matterContext.matter && (
        <CreateMatterModal
          isOpen={modals.editMatterModalOpen}
          onClose={() => {
            modals.setEditMatterModalOpen(false);
            modals.setMatterToEdit(null);
          }}
          experts={store.experts}
          matter={modals.matterToEdit ?? undefined}
          onSubmit={handleUpdateMatter}
          isSubmitting={isUpdatingMatter}
        />
      )}

      {matterContext.matter && modals.removeExpertModal && (
        <RemoveExpertModal
          isOpen={Boolean(modals.removeExpertModal)}
          onClose={() => modals.setRemoveExpertModal(null)}
          matterId={matterId}
          expertId={modals.removeExpertModal.expertId}
          expertName={
            matterContext.getExpertById(modals.removeExpertModal.expertId)
              ?.name ?? 'this expert'
          }
          isInvited={modals.removeExpertModal.isInvited}
          onRemoved={() => modals.setRemoveExpertModal(null)}
        />
      )}

      {matterContext.currentUser && chat.chatOtherParty && (
        <ChatSidebar
          open={modals.chatSidebarOpen}
          onClose={chat.closeChatSidebar}
          expert={chat.chatOtherParty}
          matter={matterContext.matter}
          currentUserId={matterContext.currentUser.id}
          currentUserEmail={matterContext.currentUser.email}
          messages={chat.chatMessagesForSidebar}
          onSendMessage={(content) => {
            chat.sendMessage(content);
          }}
          onDownloadHistory={chat.downloadHistory}
        />
      )}
    </DetailPageRoot>
  );
};

export default MatterDetail;
